import { ChevronDown, ChevronUp, UserCircle, X } from 'lucide-react'
import React, { useContext, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { FamilyContext } from '../../Context/FamilyHeadContext'
import { API_URL } from '../../services/apiService'

const DesktopFamilyTreeDropdown = ({ onMobileMenuClose }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const dropdownRef = useRef(null)
  const scrollableRef = useRef(null)
  const navigate = useNavigate()
  const { t, i18n } = useTranslation()

  const {
    familyHeadsBazach,
    familyHeadsHumenir,
    currentFamilyHead,
    loading,
    huminerMain,
    bazachMain,
  } = useContext(FamilyContext)

  useEffect(() => {
    if (isDropdownOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }

    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isDropdownOpen])

  useEffect(() => {
    if (!scrollableRef.current) return

    const scrollableArea = scrollableRef.current

    const handleWheel = (e) => {
      const { scrollTop, scrollHeight, clientHeight } = scrollableArea
      const isScrollingUp = e.deltaY < 0
      const isScrollingDown = e.deltaY > 0

      if (isScrollingUp && scrollTop === 0) {
        e.preventDefault()
      }

      if (isScrollingDown && scrollTop + clientHeight >= scrollHeight) {
        e.preventDefault()
      }
    }

    scrollableArea.addEventListener('wheel', handleWheel, { passive: false })
    return () => scrollableArea.removeEventListener('wheel', handleWheel)
  }, [])

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const getModifiedHeads = (heads, isHuminer) => {
    return heads.map((head) => ({
      ...head,
      path:
        head.gedId === (isHuminer ? huminerMain?.gedId : bazachMain?.gedId)
          ? isHuminer
            ? `${i18n.language}/huminer`
            : `${i18n.language}/`
          : isHuminer
          ? `${i18n.language}/huminer/${head.nickname}`
          : `${i18n.language}/${head.nickname}`,
    }))
  }

  const getAllHeads = () => {
    return [
      ...getModifiedHeads(familyHeadsBazach, false),
      ...getModifiedHeads(familyHeadsHumenir, true),
    ].sort((a, b) => {
      if (a.isMain && !b.isMain) return -1
      if (!a.isMain && b.isMain) return 1
      return a.name.localeCompare(b.name)
    })
  }

  const handleSelectFamilyHead = (head) => {
    navigate(head.path)
    setIsDropdownOpen(false)
    onMobileMenuClose?.()
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-2">
        <div className="w-5 h-5 border-2 border-[#8B5E3C] border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  return (
    <>
      <div className="relative w-auto">
        <button
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          className="flex w-auto items-center justify-start gap-2 px-4 py-2 rounded-full hover:bg-[#E6D5C1] transition-colors duration-300 text-[#8B5E3C] font-heebo"
        >
          {currentFamilyHead && (
            <div className="w-6 h-6 flex-shrink-0">
              {currentFamilyHead.images?.[0]?.key ? (
                <img
                    crossOrigin="use-credentials"
                  src={`${API_URL}/image/download/${encodeURIComponent(
                    currentFamilyHead.images[0].key,
                  )}`}
                  alt={currentFamilyHead.name}
                  className="w-full h-full rounded-full object-cover"
                />
              ) : (
                <UserCircle className="w-full h-full" />
              )}
            </div>
          )}
          <span className="font-bold text-base">
          {t('navbar.ancestryChoose')}
          </span>
          {isDropdownOpen ? (
            <ChevronUp className="w-4 h-4" />
          ) : (
            <ChevronDown className="w-4 h-4" />
          )}
        </button>
      </div>

      {isDropdownOpen && (
        <div
          className="overflow-hidden"
          style={{ zIndex: 99998 }}
          onClick={() => setIsDropdownOpen(false)}
        />
      )}

      <div
        className={`fixed top-0 right-0 h-screen w-1/2 bg-white shadow-xl transform transition-all duration-300 ease-in-out overflow-hidden
                    ${isDropdownOpen ? 'translate-x-0' : 'translate-x-full'}`}
        style={{ zIndex: 99999 }}
        ref={dropdownRef}
      >
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between p-6 border-b bg-white">
            <h2 className="text-xl font-bold text-[#5C4033]">
              {t('navbar.ancestryChooseTitle')}
            </h2>
            <button
              onClick={() => setIsDropdownOpen(false)}
              className="p-2 hover:bg-[#f8f5f1] rounded-full text-[#8B5E3C] hover:text-[#5C4033] transition-colors"
            >
              <X className="w-8 h-8" />
            </button>
          </div>

          <div ref={scrollableRef} className="flex-1 overflow-y-auto bg-white ">
            <div className="p-4 grid gap-3">
              {getAllHeads().map((head) => (
                <button
                  key={head.gedId}
                  onClick={() => handleSelectFamilyHead(head)}
                  className={`flex items-center gap-3 p-4 rounded-lg w-full text-right transition-all duration-300
                                        ${
                                          currentFamilyHead?.gedId ===
                                          head.gedId
                                            ? 'bg-[#f8f5f1] text-[#4a3f30]'
                                            : 'hover:bg-[#f8f5f1] text-[#8B5E3C]'
                                        }`}
                >
                  <div className="flex-shrink-0">
                    {head.images?.[0]?.key ? (
                      <img
                          crossOrigin="use-credentials"
                        src={`${API_URL}/image/download/${encodeURIComponent(
                          head.images[0].key,
                        )}`}
                        alt={head.name}
                        className="w-12 h-12 rounded-lg object-cover"
                      />
                    ) : (
                      <UserCircle className="w-12 h-12" />
                    )}
                  </div>
                  <div className="flex-grow">
                    <div className="font-bold">{head.name}</div>
                    {head.title && (
                      <div className="text-sm opacity-75">{head.title}</div>
                    )}
                  </div>
                  {head.isMain && (
                    <span className="text-xs bg-[#E6D5C1] text-[#5C4033] px-2 py-1 rounded-full">
                      {t('mainLineage.isMain')}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default DesktopFamilyTreeDropdown
